import { useEffect, useId, useImperativeHandle, useRef, useState, type Dispatch, type Ref } from "react";
import { touchScreen } from "../../lib/touch";
import type { CustomerDetail, LineRequest, PlatformCaller } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatPhone, phoneDigits, phoneOnLeave } from "../../lib/phone";
import { GrowingTextarea } from "../../ui/GrowingTextarea";
import { Suggest } from "../../ui/Suggest";
import type { Cart, CartAction, Customer } from "./cart";
import {
  activeZone,
  fromCustomer,
  lastOrderDay,
  sinceText,
  streetSuggestions,
  zoneLabel,
  zoneLine,
  zoneSuggestions,
  zoneText,
} from "./customer";

/** The sale screen puts the cursor in a field that is still missing (prototype canPay). */
export interface CustomerFieldsHandle {
  focus: (field: "zone" | "street") => void;
}

interface Props {
  cart: Cart;
  dispatch: Dispatch<CartAction>;
  /** "Herhaal": the lines of the customer's last order onto the ticket. */
  onRepeat: (lines: LineRequest[]) => void;
  /** The customer the number on the ticket belongs to, each time that changes: the folded block shows the note. */
  onKnown: (customer: CustomerDetail | null) => void;
  ref?: Ref<CustomerFieldsHandle>;
}

/** What the ticket keeps with a known customer as it is typed (prototype bindField). */
type Kept = Partial<Record<"name" | "email" | "street" | "number" | "box" | "zone" | "note", string>>;

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The customer of a takeaway or delivery order, as in the prototype (renderCust): "Telefoon + Enter"
 * saves a new number at once, a known number fills in the customer while it is typed and shows the
 * customer card, the fields of a known customer are kept with the customer, and a delivery first
 * takes the (deel)gemeente, then a street from that gemeente's list.
 */
export function CustomerFields({ cart, dispatch, onRepeat, onKnown, ref }: Props) {
  const { api, delivery, streets, customersRevision, toast, report } = useTill();
  const text = t.customer;
  const customer = cart.customer;
  const digits = phoneDigits(customer.phone);
  const full = digits.length >= 9;
  const isDelivery = cart.channel === "delivery";

  /** The answer for the number on the ticket: a customer, or null for an unknown number. */
  const [found, setFound] = useState<{
    digits: string;
    customer: CustomerDetail | null;
    /** Not a customer, but known from Takeaway.com or Mymenu.be: name and address filled in once. */
    platform?: PlatformCaller | null;
  } | null>(null);
  /** The number whose customer was filled in already: typing on never overwrites the fields again. */
  const filled = useRef<string | null>(null);
  const known = found?.digits === digits ? found.customer : null;
  const unknown = full && found?.digits === digits && found.customer === null;
  useEffect(() => {
    onKnown(known);
  }, [known, onKnown]);
  // the fields go when the order becomes a table: no customer is known then
  useEffect(
    () => () => {
      onKnown(null);
    },
    [onKnown],
  );

  const nameField = useRef<HTMLInputElement>(null);
  const zoneField = useRef<HTMLInputElement>(null);
  const streetField = useRef<HTMLInputElement>(null);
  const numberField = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => ({
    focus: (field) => {
      (field === "zone" ? zoneField : streetField).current?.focus();
    },
  }));

  // recognised while typing (prototype: CUSTOMERS[digits]); again when the customer changed elsewhere
  useEffect(() => {
    if (!full) {
      filled.current = null;
      return;
    }
    let current = true;
    api
      .request<{ customer: CustomerDetail | null; platform?: PlatformCaller | null }>(
        "GET",
        `/device/customers/lookup?phone=${digits}`,
      )
      .then((answer) => {
        if (!current) return;
        setFound({ digits, customer: answer.customer, platform: answer.platform ?? null });
        if (answer.customer && filled.current !== digits) {
          filled.current = digits;
          dispatch({ type: "fillCustomer", customer: fromCustomer(answer.customer) });
        } else if (answer.platform && filled.current !== digits) {
          // only the fields still empty: what the staff typed already stays
          filled.current = digits;
          dispatch({ type: "fillEmpty", customer: answer.platform });
        }
      })
      .catch(() => undefined); // offline: the ticket simply has no card
    return () => {
      current = false;
    };
  }, [api, digits, full, customersRevision, dispatch]);

  const set = (field: keyof Customer, value: string) => {
    dispatch({ type: "customer", field, value });
  };

  /** A field of a known customer is kept with the customer when it is left (and changed). */
  const keep = (change: Kept) => {
    if (!known) return;
    const changed = Object.entries(change).filter(([key, value]) => known[key as keyof Kept] !== value);
    if (changed.length === 0) return;
    api
      .request<{ customer: CustomerDetail }>("PATCH", `/device/customers/${known.id}`, {
        body: Object.fromEntries(changed),
      })
      .then((answer) => {
        setFound({ digits: answer.customer.phone, customer: answer.customer });
      })
      .catch(report);
  };

  const focusAfterPhone = () => {
    if (customer.name.trim() === "") nameField.current?.focus();
    else if (isDelivery) (customer.zone ? streetField : zoneField).current?.focus();
  };

  /** "Telefoon + Enter": a known number fills in the customer, a new one is saved at once. */
  const enter = async () => {
    if (!full) {
      toast(text.incomplete, "bad");
      return;
    }
    if (known) {
      dispatch({ type: "fillCustomer", customer: fromCustomer(known) });
      focusAfterPhone();
      return;
    }
    try {
      const answer = await api.request<{ customer: CustomerDetail; created: boolean }>("POST", "/device/customers", {
        body: {
          phone: digits,
          name: customer.name.trim(),
          email: customer.email.trim(),
          street: customer.street.trim(),
          number: customer.number.trim(),
          box: customer.box.trim(),
          zone: customer.zone.trim(),
        },
      });
      filled.current = digits;
      setFound({ digits, customer: answer.customer });
      if (answer.created) {
        set("phone", formatPhone(digits));
        toast(text.saved(formatPhone(digits)));
      } else {
        dispatch({ type: "fillCustomer", customer: fromCustomer(answer.customer) });
      }
      focusAfterPhone();
    } catch (error) {
      report(error);
    }
  };

  // the zone field: "9140 Temse" once chosen, what is typed meanwhile (prototype zdraw / zpick)
  const [zoneTyped, setZoneTyped] = useState<string | null>(null);
  const chosenZone = activeZone(delivery, customer.zone);
  const zoneShown = zoneTyped ?? (chosenZone ? zoneText(chosenZone) : customer.zone);
  const zones = zoneSuggestions(delivery, zoneTyped ?? zoneShown);
  /**
   * A suggestion was just taken: the field loses the focus to the next one before the ticket shows
   * the choice, so leaving it must not save the half-typed text over it.
   */
  const picked = useRef(false);
  const pickZone = (name: string) => {
    setZoneTyped(null);
    set("zone", name);
    keep({ zone: name });
    picked.current = true;
    streetField.current?.focus();
  };

  const line = isDelivery && customer.zone ? zoneLine(delivery, customer.zone, customer.street, streets) : null;
  const [intern, setIntern] = useState<string | null>(null);
  const noteId = useId();
  const internId = useId();

  return (
    <>
      <div className="row">
        <input
          value={customer.phone}
          placeholder={touchScreen() ? text.phoneTouch : text.phone}
          aria-label={text.phoneLabel}
          inputMode="tel"
          maxLength={40}
          onChange={(event) => {
            set("phone", event.target.value);
          }}
          onBlur={() => {
            const written = phoneOnLeave(customer.phone);
            if (written !== null) set("phone", written);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            void enter();
          }}
        />
        <input
          ref={nameField}
          value={customer.name}
          placeholder={text.name}
          aria-label={text.name}
          maxLength={100}
          onChange={(event) => {
            set("name", event.target.value);
          }}
          onBlur={() => {
            keep({ name: customer.name.trim() });
          }}
        />
      </div>
      <input
        type="email"
        value={customer.email}
        placeholder={text.email}
        aria-label={text.emailLabel}
        autoComplete="off"
        maxLength={200}
        onChange={(event) => {
          set("email", event.target.value);
        }}
        onBlur={() => {
          const email = customer.email.trim();
          if (email && !EMAIL.test(email)) {
            if (known) toast(t.customersAdmin.dialog.badEmail, "bad");
            return;
          }
          keep({ email });
        }}
      />
      {isDelivery && (
        <>
          <div className="row">
            <Suggest
              inputRef={zoneField}
              style={{ flex: 1 }}
              value={zoneShown}
              placeholder={text.zone}
              label={text.zoneLabel}
              suggestions={zones.map((zone) => ({ key: zone.id, label: zone.name, hint: zone.postcode }))}
              openOnFocus
              tabPicks
              onText={(typed) => {
                setZoneTyped(typed);
                if (customer.zone) set("zone", "");
              }}
              onPick={(suggestion) => {
                const zone = zones.find((item) => item.id === suggestion.key);
                pickZone(zone && delivery ? zoneLabel(zone, delivery.zones) : suggestion.label);
              }}
              onLeave={() => {
                if (picked.current) {
                  picked.current = false;
                  return;
                }
                // a single zone left for what was typed: that one (prototype)
                if (customer.zone || !zoneTyped?.trim()) return;
                const only = zoneSuggestions(delivery, zoneTyped);
                if (only.length === 1 && delivery) {
                  const label = zoneLabel(only[0]!, delivery.zones);
                  setZoneTyped(null);
                  set("zone", label);
                  keep({ zone: label });
                }
              }}
            />
          </div>
          <div className="row">
            <Suggest
              inputRef={streetField}
              style={{ flex: 2.2 }}
              value={customer.street}
              placeholder={chosenZone ? text.streetIn(chosenZone.name) : text.street}
              label={text.street}
              suggestions={streetSuggestions(streets, customer.zone, customer.street).map((street) => ({
                key: `${street.zone}|${street.name}`,
                label: street.name,
                hint: street.zone,
              }))}
              onText={(typed) => {
                set("street", typed);
              }}
              onPick={(suggestion) => {
                // a street of another zone than the one chosen: its zone comes with it
                const zone = suggestion.key.slice(0, suggestion.key.indexOf("|"));
                if (zone.toLowerCase() !== customer.zone.trim().toLowerCase()) {
                  setZoneTyped(null);
                  set("zone", zone);
                }
                set("street", suggestion.label);
                keep({
                  street: suggestion.label,
                  ...(zone.toLowerCase() !== customer.zone.trim().toLowerCase() ? { zone } : {}),
                });
                picked.current = true;
                numberField.current?.focus();
              }}
              onEnter={() => numberField.current?.focus()}
              onLeave={() => {
                if (picked.current) picked.current = false;
                else keep({ street: customer.street.trim() });
              }}
            />
            <input
              ref={numberField}
              style={{ flex: 0.6 }}
              value={customer.number}
              placeholder={text.number}
              aria-label={text.numberLabel}
              maxLength={20}
              onChange={(event) => {
                set("number", event.target.value);
              }}
              onBlur={() => {
                keep({ number: customer.number.trim() });
              }}
            />
            <input
              style={{ flex: 0.6 }}
              value={customer.box}
              placeholder={text.box}
              aria-label={text.box}
              maxLength={20}
              onChange={(event) => {
                set("box", event.target.value);
              }}
              onBlur={() => {
                keep({ box: customer.box.trim() });
              }}
            />
          </div>
          <div className={`hint${customer.zone ? "" : " newc"}`}>
            {customer.zone === "" ? (
              text.chooseZoneFirst
            ) : line ? (
              <>
                {line.text}
                {line.outside && (
                  <>
                    {" · "}
                    <b>{text.zoneLine.outside}</b>
                  </>
                )}
              </>
            ) : null}
          </div>
        </>
      )}
      <label className="flabel" htmlFor={noteId}>
        {text.note} <span>{text.noteHint}</span>
      </label>
      <GrowingTextarea
        id={noteId}
        value={cart.note}
        placeholder={text.notePlaceholder}
        maxLength={500}
        onChange={(value) => {
          dispatch({ type: "note", value });
        }}
      />
      {known ? (
        <CustomerCard
          customer={known}
          onRepeat={() => {
            if (known.lastOrder) onRepeat(known.lastOrder.lines);
          }}
        />
      ) : (
        unknown && (
          <div className="hint newc">
            {found.platform && <>{text.fromPlatform(found.platform.orders, found.platform.platform)} </>}
            {text.unknown} <b>Enter</b> {text.unknownAfter}
          </div>
        )
      )}
      {known && (
        <>
          <label className="flabel intern" htmlFor={internId}>
            {text.intern} <span>{text.internHint}</span>
          </label>
          <GrowingTextarea
            id={internId}
            className="intern"
            value={intern ?? known.note}
            placeholder={text.internPlaceholder}
            maxLength={500}
            onChange={setIntern}
            onBlur={() => {
              if (intern !== null) keep({ note: intern.trim() });
              setIntern(null);
            }}
          />
        </>
      )}
    </>
  );
}

/** The customer card (prototype custCard): orders, average, since, the loyalty card, the last order. */
function CustomerCard({ customer, onRepeat }: { customer: CustomerDetail; onRepeat: () => void }) {
  const { promotions } = useTill();
  const text = t.customer.card;
  const action = promotions?.points;
  const goal = action?.goal ?? 11;
  const points = Math.min(customer.points, goal);
  return (
    <div className="custcard" aria-label={text.label}>
      <div className="cc-top">
        <b>{customer.name || text.noName}</b>
        <span className={`pill ${customer.orders > 0 ? "ok" : "warn"}`}>
          {customer.orders > 0 ? text.regular : text.new}
        </span>
      </div>
      {customer.orders > 0 && (
        <div className="cc-stats num">
          <span>
            <b>{customer.orders}</b> {text.orders}
          </span>
          {customer.averageCents !== null && (
            <span>
              <b>{formatEuro(customer.averageCents)}</b> {text.average}
            </span>
          )}
          {customer.since !== null && <span>{text.since(sinceText(customer.since))}</span>}
        </div>
      )}
      {action?.on === true && (
        <div className="cc-pts">
          <span className="dots" role="img" aria-label={text.points(customer.points, goal)}>
            {Array.from({ length: goal }, (_, index) => (
              <i key={index} className={index < points ? "on" : undefined} />
            ))}
          </span>
          <span className="num">
            {customer.points}/{goal}
            {customer.points >= goal ? text.ready(formatEuro(action.rewardCents)) : ""}
          </span>
        </div>
      )}
      {customer.lastOrder && (
        <div className="cc-last">
          <span>
            {text.last(lastOrderDay(customer.lastOrder.at))}
            {customer.lastOrder.text}
          </span>
          <button type="button" className="btn ghost" onClick={onRepeat}>
            {text.repeat}
          </button>
        </div>
      )}
    </div>
  );
}
